/**
 * The shopping board — the first thing this module shipped, because the live problem
 * is a coverage gap with quotes about to be in flight, and a policy registry does not
 * help with that.
 *
 * Three states of an ask, all visible at once: waiting to hear back, on the table, and
 * history (expired or declined). The history is not clutter — four carriers have already
 * declined this household's home risk, and knowing that is what stops the next round
 * starting with the same four phone calls.
 *
 * Scope honesty: this screen knows about QUOTES, not about coverage in force. It never
 * says the house is covered — the policy registry owns that question and lands next.
 */
import { useEffect, useRef, useState } from 'react';
import { api, money } from '../../lib/api';
import type {
  InsuranceContact,
  InsuranceQuote,
  InsuranceTypeConfig,
  QuoteComparison,
} from '../../shared/types';
import CompareTable from './CompareTable';
import QuoteForm from './QuoteForm';
import {
  Button,
  Card,
  Chip,
  Disclaimer,
  EmptyState,
  ErrorState,
  Loading,
  PanelHeading,
  cx,
  useResource,
  validityLabel,
  validityTone,
  type Tone,
} from './ui';

interface Bundle {
  comparison: QuoteComparison;
  quotes: InsuranceQuote[];
  contacts: InsuranceContact[];
}

/** Whole days since an ISO date, or null. Display only — no decision hangs on it. */
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  const then = Date.UTC(y, m - 1, d);
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((today - then) / 86_400_000);
}

export default function QuotesPanel({ types }: { types: InsuranceTypeConfig[] }) {
  const [policyType, setPolicyType] = useState(types[0]?.slug ?? 'homeowners');
  const [editing, setEditing] = useState<InsuranceQuote | null>(null);
  const [adding, setAdding] = useState(false);

  const { data, loading, error, reload } = useResource<Bundle>(
    async () => {
      const [comparison, quotes, contacts] = await Promise.all([
        api.get<QuoteComparison>(
          `/insurance/quotes/compare?policy_type=${encodeURIComponent(policyType)}`
        ),
        api.get<InsuranceQuote[]>(`/insurance/quotes?policy_type=${encodeURIComponent(policyType)}`),
        api.get<InsuranceContact[]>('/insurance/contacts'),
      ]);
      return { comparison, quotes, contacts };
    },
    [policyType]
  );

  const typeLabel = types.find((t) => t.slug === policyType)?.label ?? policyType;
  const quotesExist = (data?.quotes.length ?? 0) > 0;

  // Opening the form has to be visible. On a phone the Edit button can be well below the
  // form, so tapping it looked like nothing happened — and autoFocus only fires on mount,
  // so switching between quotes gave no cue either.
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (adding || editing) {
      formRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [adding, editing]);

  function closeForm() {
    setAdding(false);
    setEditing(null);
  }

  function afterSave() {
    closeForm();
    reload();
  }

  return (
    <section>
      <PanelHeading
        title="Shopping board"
        sub="Every ask out, every quote in, and how each one measures against your checklist."
      />

      {/*
        Line of coverage. From D1 config, so a new line appears the moment it is added in
        settings — never a hardcoded list.

        This is a FILTER, not a tabbed view: there is no panel and the URL doesn't change.
        It used to carry role="tab"/"tablist", which promises arrow-key navigation and a
        tabpanel relationship that don't exist, and announces "tab, 1 of 7". role="group"
        with aria-pressed describes what it actually is and keeps plain Tab working.
      */}
      <div role="group" aria-label="Line of coverage" className="mb-5 flex flex-wrap gap-2">
        {types.map((t) => (
          <button
            key={t.slug}
            type="button"
            aria-pressed={t.slug === policyType}
            onClick={() => {
              setPolicyType(t.slug);
              closeForm();
            }}
            className={cx(
              'min-h-11 rounded-full border px-4 text-sm font-medium transition-colors',
              'focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
              'focus-visible:ring-offset-canvas focus-visible:outline-none',
              t.slug === policyType
                ? 'border-sage bg-sage-soft text-sage'
                : 'border-border bg-surface text-ink-soft hover:bg-surface-sunk hover:text-ink'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/*
        The action sits AFTER the selector, not in the heading. PanelHeading's flex wraps at
        375px, which put "Log a quote" above the line-of-coverage chips — so the first tap on
        a phone filed a quote against whatever type happened to be first. Context before
        action. Disabled while loading, because `requirements` is still [] and the form would
        open with no "What it covers" fields.
      */}
      {!adding && !editing && quotesExist && (
        <div className="mb-5">
          <Button variant="primary" onClick={() => setAdding(true)} disabled={loading}>
            Log a quote or request
          </Button>
        </div>
      )}

      {(adding || editing) && (
        <div ref={formRef}>
          <Card className="mb-6">
            <h3 className="mb-4 font-display text-lg font-semibold text-ink">
              {editing
                ? `Edit — ${editing.carrier}`
                : `New ${typeLabel.toLowerCase()} quote or request`}
            </h3>
            {/* The key is load-bearing, not decoration. QuoteForm seeds its state from the
                `quote` prop in useState initializers, which run ONLY on mount. Without a key
                that changes with the target, clicking Edit on one quote while another's form
                is already open reuses the same instance: the inputs keep the first quote's
                values while the prop points at the second, and saving would PUT the first
                quote's data over the second one's row. Remounting is the fix. */}
            <QuoteForm
              key={editing ? `edit-${editing.id}` : 'new'}
              policyType={policyType}
              requirements={data?.comparison.requirements ?? []}
              contacts={data?.contacts ?? []}
              quote={editing}
              onSaved={afterSave}
              onCancel={closeForm}
            />
          </Card>
        </div>
      )}

      {loading && <Loading label={`Loading ${typeLabel} quotes`} />}
      {error && !loading && <ErrorState message={error} onRetry={reload} />}

      {data && !loading && !error && (
        <Body
          data={data}
          typeLabel={typeLabel}
          onAdd={() => setAdding(true)}
          onEdit={setEditing}
        />
      )}
    </section>
  );
}

function Body({
  data,
  typeLabel,
  onAdd,
  onEdit,
}: {
  data: Bundle;
  typeLabel: string;
  onAdd: () => void;
  onEdit: (q: InsuranceQuote) => void;
}) {
  const { quotes, comparison } = data;

  const live = quotes.filter((q) => q.status === 'received' && !q.is_expired);
  const waiting = quotes.filter((q) => q.status === 'requested');
  const history = quotes.filter(
    (q) => q.status === 'declined' || q.status === 'expired' || q.status === 'bound'
  );
  const declined = history.filter((q) => q.status === 'declined');
  const expiring = live.filter((q) => q.days_valid !== null && q.days_valid <= 7);

  if (quotes.length === 0) {
    return (
      <EmptyState
        title={`No ${typeLabel.toLowerCase()} quotes on the board`}
        action={
          <Button variant="primary" onClick={onAdd}>
            Log the first request
          </Button>
        }
      >
        Log a request the moment you make the call — carrier, who you spoke to, and the date.
      </EmptyState>
    );
  }

  return (
    <div className="space-y-6">
      <Headline
        typeLabel={typeLabel}
        live={live.length}
        expiring={expiring.length}
        waiting={waiting.length}
        declined={declined.length}
        expired={history.filter((q) => q.status === 'expired').length}
      />

      {comparison.columns.length > 0 && comparison.requirements.length > 0 && (
        <div>
          <h3 className="mb-3 font-display text-lg font-semibold text-ink">
            Measured against your checklist
          </h3>
          <CompareTable comparison={comparison} />
          <Disclaimer />
        </div>
      )}

      {/* Reachable today: only homeowners and auto ship with a checklist. No link to an
          editor here on purpose — the requirements admin screen is a later slice, and a
          button that goes nowhere is worse than none. */}
      {comparison.requirements.length === 0 && (
        <Card tone="sunk">
          <p className="text-sm text-ink-soft">
            No checklist for {typeLabel.toLowerCase()} yet, so these quotes are listed but not
            scored.
          </p>
        </Card>
      )}

      {waiting.length > 0 && (
        <QuoteList
          title="Waiting to hear back"
          sub="An ask is out. Nothing to compare until it lands."
          quotes={waiting}
          contacts={data.contacts}
          onEdit={onEdit}
        />
      )}

      {live.length > 0 && (
        <QuoteList
          title="On the table"
          sub="Quotes you could still act on."
          quotes={live}
          contacts={data.contacts}
          comparison={comparison}
          onEdit={onEdit}
        />
      )}

      {history.length > 0 && (
        <details className="rounded-card border border-border bg-surface">
          <summary className="cursor-pointer p-4 text-sm font-medium text-ink">
            History — {history.length} closed {history.length === 1 ? 'entry' : 'entries'}
          </summary>
          <div className="border-t border-border p-4">
            <QuoteList
              title=""
              quotes={history}
              contacts={data.contacts}
              onEdit={onEdit}
            />
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * The honest one-line state of the search. Deliberately says nothing about whether the
 * house is insured — this screen only knows what has been quoted.
 */
function Headline({
  typeLabel,
  live,
  expiring,
  waiting,
  declined,
  expired,
}: {
  typeLabel: string;
  live: number;
  expiring: number;
  waiting: number;
  declined: number;
  expired: number;
}) {
  const tone: Tone = live === 0 ? 'over' : expiring > 0 ? 'near' : 'ok';

  const headline =
    live === 0
      ? `Nothing bindable on the board for ${typeLabel.toLowerCase()}.`
      : expiring > 0
        ? `${live} quote${live === 1 ? '' : 's'} on the table, ${expiring} expiring within a week.`
        : `${live} quote${live === 1 ? '' : 's'} on the table.`;

  const parts: string[] = [];
  if (waiting > 0) parts.push(`${waiting} request${waiting === 1 ? '' : 's'} still out`);
  if (expired > 0) parts.push(`${expired} expired unbound`);
  if (declined > 0) parts.push(`${declined} carrier${declined === 1 ? '' : 's'} declined`);

  return (
    <Card tone={tone}>
      <p className="font-display text-lg leading-snug font-semibold text-ink">{headline}</p>
      {parts.length > 0 && <p className="mt-1 text-sm text-ink-soft">{parts.join(' · ')}.</p>}
      {live === 0 && (
        <p className="mt-2 max-w-prose text-sm text-ink-soft">
          Whether the house is actually covered right now is a separate question — this board
          only tracks quotes.
        </p>
      )}
    </Card>
  );
}

function QuoteList({
  title,
  sub,
  quotes,
  contacts,
  comparison,
  onEdit,
}: {
  title: string;
  sub?: string;
  quotes: InsuranceQuote[];
  contacts: InsuranceContact[];
  comparison?: QuoteComparison;
  onEdit: (q: InsuranceQuote) => void;
}) {
  return (
    <div>
      {title && (
        <div className="mb-3">
          <h3 className="font-display text-lg font-semibold text-ink">{title}</h3>
          {sub && <p className="text-sm text-ink-soft">{sub}</p>}
        </div>
      )}

      <ul className="space-y-3">
        {quotes.map((q) => {
          const contact = contacts.find((c) => c.id === q.requested_contact_id);
          const col = comparison?.columns.find((c) => c.quote.id === q.id);
          const waited = q.status === 'requested' ? daysSince(q.requested_date) : null;
          const validity = validityLabel(q.days_valid);

          return (
            <Card as="li" key={q.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display font-semibold text-ink">{q.carrier}</p>
                  <p className="mt-0.5 text-sm text-ink-soft">
                    {q.agency ? `via ${q.agency}` : 'direct'}
                    {q.quote_number ? ` · ${q.quote_number}` : ''}
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {q.status === 'declined' && <Chip tone="over">Declined</Chip>}
                    {q.status === 'bound' && <Chip tone="ok">Bound</Chip>}
                    {validity && <Chip tone={validityTone(q.days_valid)}>{validity}</Chip>}
                    {waited !== null && waited >= 0 && (
                      <Chip tone={waited >= 7 ? 'near' : 'neutral'}>
                        asked {waited === 0 ? 'today' : `${waited}d ago`}
                      </Chip>
                    )}
                    {col && col.dealbreakers > 0 && (
                      <Chip tone="over">
                        {col.dealbreakers} dealbreaker{col.dealbreakers === 1 ? '' : 's'}
                      </Chip>
                    )}
                    {col && col.unknown > 0 && (
                      <Chip tone="neutral">{col.unknown} unanswered</Chip>
                    )}
                  </div>

                  {contact && (
                    <p className="mt-2 text-sm text-ink-soft">
                      Asked {contact.name}
                      {contact.org ? ` at ${contact.org}` : ''}
                      {contact.phone && (
                        <>
                          {' · '}
                          <a
                            href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}
                            className="inline-flex min-h-11 items-center underline underline-offset-2 hover:text-ink"
                          >
                            {contact.phone}
                          </a>
                        </>
                      )}
                    </p>
                  )}

                  {q.outcome_reason && (
                    <p className="mt-2 max-w-prose text-sm leading-relaxed text-ink-soft">
                      {q.outcome_reason}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-end gap-2">
                  <span className="font-display text-lg font-semibold text-ink tabular-nums">
                    {q.total_cents === null ? '—' : money(q.total_cents)}
                  </span>
                  <Button variant="secondary" onClick={() => onEdit(q)}>
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </ul>
    </div>
  );
}
